import type {Auth} from 'src/typings';
import type {CurrentAuthService} from 'shared/services';
import type {ToastMessageService} from 'shared/components/kp-toast-messages';
import type {IHttpResponse, IHttpService, IWindowService} from 'angular';
import type {LocalizationService} from '../providers/localization.provider';
import type ReloaderService from '../services/reloader.service';
import type MainLoginFormService from './main-login-form-service';
import type {Observable} from 'rxjs';
import {LoginValidityChecker} from 'shared/login/login-validity-checker';
import {Subject} from 'rxjs';
import {ngAsync} from '../utils/ng-@decorators';
import {exists} from 'shared/utils/custom-utils';

/**
 * @ngdoc service
 * @name loginService
 * @module portaro.login
 */
export default class LoginService {
    public static serviceName = 'loginService';

    private changedButNotReloaded: boolean;
    private readonly listeners: LoginListener[] = []; // only to delay reloder until all async hooks ran, remove after reloader is no longer needed.
    private readonly loginEventSubject$: Subject<Auth>;
    private readonly loginValidityChecker: LoginValidityChecker;

    /*@ngInject*/
    constructor(private currentAuth: Auth,
                private currentAuthService: CurrentAuthService,
                private mainLoginFormService: MainLoginFormService,
                private reloader: ReloaderService,
                private $window: IWindowService,
                private $http: IHttpService,
                localizationService: LocalizationService,
                toastMessageService: ToastMessageService) {

        this.loginValidityChecker = new LoginValidityChecker(toastMessageService, this, currentAuthService, localizationService);
        this.loginEventSubject$ = new Subject();
    }

    /**
     * @deprecated use {@link loginEventObservable$}
     */
    public registerListener(listener: LoginListener): void {
        const exist = this.listeners.includes(listener);
        if (!exist) {
            this.listeners.push(listener);
        }
    }

    /**
     * Refreshes current auth from backend
     */
    @ngAsync()
    public async refresh(): Promise<Auth> {
        const {data: newAuth}: IHttpResponse<Auth> = await this.$http.get('/api/auth/current');
        this.applyNewAuth(newAuth);
        await this.notify(newAuth);

        this.updateAuthValidity(newAuth);

        return newAuth;
    }

    /**
     * This method should open login form
     */
    @ngAsync()
    public async login(): Promise<Auth> {
        if (this.currentAuth.evided) {
            return this.currentAuth;
        }

        const newAuth: Auth = await this.mainLoginFormService.openForm();
        this.applyNewAuth(newAuth);
        await this.notify(newAuth);

        // Push new login event
        this.loginEventSubject$.next(newAuth);

        this.updateAuthValidity(newAuth);

        return newAuth;
    }

    /**
     * This method should re-login user
     */
    public async relogin(reloginAfterSessionTimeout: boolean = false): Promise<Auth> {
        if (this.currentAuth.evided) {
            if (reloginAfterSessionTimeout) {
                this.currentAuth.evided = false;
            } else {
                await this.logout();
            }
        }
        return this.login();
    }

    @ngAsync()
    public async upgrade(): Promise<Auth> {
        await this.login();
        await this.$http.post('/api/auth/multifactor/upgrade', {});
        return this.refresh();
    }

    /**
     * This method should log user out
     */
    @ngAsync()
    public async logout(path: string | null = null, refresh: boolean = true): Promise<void> {
        await this.$http.get('/logout');
        this.currentAuth.evided = false;

        const {data: newAuth}: IHttpResponse<Auth> = await this.$http.get('/api/auth/current');
        this.currentAuthService.notifyNewAuth(newAuth);
        this.updateAuthValidity(newAuth);

        if (refresh) {
            if (exists(path)) {
                // pokud je path "", zmenime na "/"
                this.$window.location.href = path || '/';
            } else {
                this.reloader.reloadPage();
            }
        }
    }

    public async goLogged(path: string) {
        try {
            await this.login();
            this.$window.location.href = path;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
            // ignore login error, do nothing
        }
    }

    public async thisPageLogged() {
        if (!this.currentAuth.evided) {
            try {
                await this.login();
                this.reloader.reloadPage();
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (e) {
                // ignore login error, do nothing
            }
        }
    }

    /**
     * This method checks if user was logged in recently and page was not reloaded
     * and if true reloads the page to update page elements dependent to user authentication
     */
    public reloadIfChangedButNotReloaded() {
        if (this.changedButNotReloaded) {
            this.reloader.reloadPage();
        }
    }

    // Updates observable in CurrentAuthService with new auth object
    private async notify(auth: Auth) {
        for (const listener of this.listeners) {
            await listener.update(auth);
        }
        this.currentAuthService.notifyNewAuth(auth);
    }

    private applyNewAuth(auth: Auth) {
        for (const key in auth) {
            if (Object.hasOwn(auth, key)) {
                this.currentAuth[key] = auth[key];
            }
        }

        this.changedButNotReloaded = true;
    }

    // Returns login event observable
    public get loginEventObservable$(): Observable<Auth> {
        return this.loginEventSubject$.asObservable();
    }

    public get validityChecker(): LoginValidityChecker {
        return this.loginValidityChecker;
    }

    private updateAuthValidity(newAuth: Auth) {
        if (newAuth.evided) {
            this.loginValidityChecker.updateValidityInStorage();
        } else {
            this.loginValidityChecker.removeValidityFromStorage();
        }
    }
}


export interface LoginListener {
    update(currentAuth: Auth): Promise<void>;
}